import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getUserNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead,
  getUnreadNotificationCount,
  deleteNotification
} from '../../services/notificationService';
import NotificationItem from './NotificationItem';
import GuestAccountMergeNotificationItem from '../guest/GuestAccountMergeNotificationItem';
import GuestEmailAssignmentRequestNotificationItem from '../guest/GuestEmailAssignmentRequestNotificationItem';
import GuestMergeDeclinedNotificationItem from '../guest/GuestMergeDeclinedNotificationItem';
import ClubJoinRequestNotificationItem from '../clubs/ClubJoinRequestNotificationItem';
import ClubJoinRequestApprovedNotificationItem from '../clubs/ClubJoinRequestApprovedNotificationItem';
import ClubJoinRequestRejectedNotificationItem from '../clubs/ClubJoinRequestRejectedNotificationItem';
import ClubJoinRequestWithdrawnNotificationItem from '../clubs/ClubJoinRequestWithdrawnNotificationItem';
import ClubReactivationNotificationItem from '../clubs/ClubReactivationNotificationItem';
import Button from '../common/Button';
import { Heading, Text, Typography } from '../common/Typography';
import { Card } from '../common/Card';
import Pagination from '../common/Pagination';

/**
 * NotificationsTab Component
 * Displays user notifications with pagination and filters
 */
const NotificationsTab = ({ currentUser, onClose }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  const pageSize = 20;

  useEffect(() => {
    if (currentUser?.id) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [currentUser?.id, page, filter]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        is_archived: false  // Always exclude archived notifications
      };
      if (filter === 'unread') {
        filters.is_read = false;
      } else if (filter === 'read') {
        filters.is_read = true;
      }

      const result = await getUserNotifications(currentUser.id, page, pageSize, filters);
      setNotifications(result.notifications);
      setTotalPages(result.totalPages);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const count = await getUnreadNotificationCount(currentUser.id);
      setUnreadCount(count);
    } catch (err) {
      console.error('Error loading unread count:', err);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      await loadNotifications();
      await loadUnreadCount();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(currentUser.id);
      await loadNotifications();
      await loadUnreadCount();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleDismiss = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      await loadNotifications();
      await loadUnreadCount();
    } catch (err) {
      console.error('Error dismissing notification:', err);
    }
  };

  const handleNotificationAction = (notification) => {
    // Handle different notification types
    if (notification.type === 'guest_account_merge') {
      navigate('/profile');
    }
    // Add other notification type handlers as needed
  };

  if (loading && notifications.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <Text className="text-text-muted">Loading notifications...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card variant="danger" size="medium">
        <Text className="text-danger-dark">{error}</Text>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Heading level={2} className="mb-1">Notifications</Heading>
          {unreadCount > 0 && (
            <Text className="text-sm text-text-muted">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </Text>
          )}
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="small"
            onClick={handleMarkAllAsRead}
          >
            Mark all as read
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <button
          onClick={() => {
            setFilter('all');
            setPage(1);
          }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border ${
            filter === 'all'
              ? 'bg-primary text-text-inverse border-primary'
              : 'bg-surface-subtle text-text-secondary border-border-medium hover:bg-[var(--color-hover)]'
          }`}
        >
          All
        </button>
        <button
          onClick={() => {
            setFilter('unread');
            setPage(1);
          }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border ${
            filter === 'unread'
              ? 'bg-primary text-text-inverse border-primary'
              : 'bg-surface-subtle text-text-secondary border-border-medium hover:bg-[var(--color-hover)]'
          }`}
        >
          Unread {unreadCount > 0 && `(${unreadCount})`}
        </button>
        <button
          onClick={() => {
            setFilter('read');
            setPage(1);
          }}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border ${
            filter === 'read'
              ? 'bg-primary text-text-inverse border-primary'
              : 'bg-surface-subtle text-text-secondary border-border-medium hover:bg-[var(--color-hover)]'
          }`}
        >
          Read
        </button>
      </div>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <Card variant="default" size="medium">
          <div className="text-center py-8">
            <Text className="text-text-muted">
              {filter === 'unread' 
                ? 'No unread notifications' 
                : filter === 'read'
                ? 'No read notifications'
                : 'No notifications'}
            </Text>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => {
            // Use enhanced component for guest account merge notifications
            if (notification.type === 'guest_account_merge') {
              return (
                <GuestAccountMergeNotificationItem
                  key={notification.id}
                  notification={notification}
                  currentUser={currentUser}
                  onUpdate={loadNotifications}
                  onClose={onClose}
                />
              );
            }
            
            // Use enhanced component for email assignment request notifications
            if (notification.type === 'guest_email_assignment_request') {
              return (
                <GuestEmailAssignmentRequestNotificationItem
                  key={notification.id}
                  notification={notification}
                  currentUser={currentUser}
                  onUpdate={loadNotifications}
                  onClose={onClose}
                />
              );
            }
            
            // Use enhanced component for declined merge notifications
            if (notification.type === 'guest_merge_declined') {
              return (
                <GuestMergeDeclinedNotificationItem
                  key={notification.id}
                  notification={notification}
                  currentUser={currentUser}
                  onUpdate={loadNotifications}
                  onClose={onClose}
                />
              );
            }
            
            // Use enhanced component for club join request notifications
            if (notification.type === 'club_join_request') {
              return (
                <ClubJoinRequestNotificationItem
                  key={notification.id}
                  notification={notification}
                  currentUser={currentUser}
                  onUpdate={loadNotifications}
                  onClose={onClose}
                />
              );
            }
            
            // Use enhanced component for approved join request notifications
            if (notification.type === 'club_join_request_approved') {
              return (
                <ClubJoinRequestApprovedNotificationItem
                  key={notification.id}
                  notification={notification}
                  currentUser={currentUser}
                  onUpdate={loadNotifications}
                  onClose={onClose}
                />
              );
            }
            
            // Use enhanced component for rejected join request notifications
            if (notification.type === 'club_join_request_rejected') {
              return (
                <ClubJoinRequestRejectedNotificationItem
                  key={notification.id}
                  notification={notification}
                  currentUser={currentUser}
                  onUpdate={loadNotifications}
                  onClose={onClose}
                />
              );
            }
            
            // Use enhanced component for withdrawn join request notifications
            if (notification.type === 'club_join_request_withdrawn') {
              return (
                <ClubJoinRequestWithdrawnNotificationItem
                  key={notification.id}
                  notification={notification}
                  currentUser={currentUser}
                  onUpdate={loadNotifications}
                  onClose={onClose}
                />
              );
            }
            
            if (
              notification.type === 'club_reactivation_requested' ||
              notification.type === 'club_reactivation_approved' ||
              notification.type === 'club_reactivation_declined'
            ) {
              return (
                <ClubReactivationNotificationItem
                  key={notification.id}
                  notification={notification}
                  onUpdate={loadNotifications}
                  onClose={onClose}
                />
              );
            }

            // Use standard component for other notifications
            return (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={handleMarkAsRead}
                onDismiss={handleDismiss}
                onAction={handleNotificationAction}
              />
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;

